import React from "react";
import {
  SimpleGrid,
  Box,
  Text,
  Icon,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaUserTie,
  FaBuilding,
  FaCheck,
  FaClock,
  FaTimes,
  FaFileAlt,
} from "react-icons/fa";

// Motion components
const MotionBox = motion(Box);

// Glass Morphism Card Component
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl", transform: "translateY(-2px)" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const StatCard = ({ title, value, icon, color, delay = 0 }) => {
  const textColor = useColorModeValue("gray.700", "white");

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      <GlassMorphismCard p={6} h="full">
        <VStack spacing={4} align="center">
          <Box
            p={3}
            borderRadius="full"
            bg={`${color}.100`}
            color={`${color}.600`}
          >
            <Icon as={icon} boxSize={8} />
          </Box>
          <VStack spacing={1} textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color={textColor}>
              {value}
            </Text>
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {title}
            </Text>
          </VStack>
        </VStack>
      </GlassMorphismCard>
    </MotionBox>
  );
};

const PayeesSummary = ({ payeesData }) => {
  if (!payeesData || payeesData.length === 0) {
    return null;
  }

  // Calculate statistics
  const totalPayees = payeesData.length;
  const adminCount = payeesData.filter(p => p.Role === "admin").length;
  const propertyOwnerCount = payeesData.filter(p => p.Role === "property_owner").length;
  
  const verifiedCount = payeesData.filter(p => p.VerificationStatus === "verified").length;
  const pendingCount = payeesData.filter(p => p.VerificationStatus === "pending").length;
  const rejectedCount = payeesData.filter(p => p.VerificationStatus === "rejected").length;
  const notInitiatedCount = payeesData.filter(p => p.VerificationStatus === "not_initiated").length;

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4, xl: 7 }} spacing={4} w="100%">
      <StatCard
        title="Total Payees"
        value={totalPayees}
        icon={FaUsers}
        color="blue"
        delay={0.1}
      />
      <StatCard
        title="Admins"
        value={adminCount}
        icon={FaUserTie}
        color="purple"
        delay={0.2}
      />
      <StatCard
        title="Property Owners"
        value={propertyOwnerCount}
        icon={FaBuilding}
        color="teal"
        delay={0.3}
      />
      <StatCard
        title="Verified"
        value={verifiedCount}
        icon={FaCheck}
        color="green"
        delay={0.4}
      />
      <StatCard
        title="Pending"
        value={pendingCount}
        icon={FaClock}
        color="yellow"
        delay={0.5}
      />
      <StatCard
        title="Rejected"
        value={rejectedCount}
        icon={FaTimes}
        color="red"
        delay={0.6}
      />
      <StatCard
        title="Not Initiated"
        value={notInitiatedCount}
        icon={FaFileAlt}
        color="gray"
        delay={0.7}
      />
    </SimpleGrid>
  );
};

export default PayeesSummary;
